'use client';

/**
 * SplashIntro — intro de marque Aureus Social Pro (rotation 22 animations).
 *
 * À chaque visite (1×/session navigateur), UNE des 22 intros est tirée au hasard.
 *
 * ─ 2D (1-12) ─
 *   1. Jarvis HUD      — réacteur + séquence de boot des modules
 *   2. Cerveau éclairs — réseau neuronal doré + arcs électriques
 *   3. Tempête         — la foudre stampe les modules un par un
 *   4. Vortex doré     — particules aspirées → explosion en logo
 *   5. Pluie de données— Matrix belge (€, CP, DmfA) → condensation logo
 *   6. Typo impact     — mots qui s'écrasent à l'écran
 *   7. Hyperespace     — saut en warp à travers un tunnel d'étoiles
 *   8. Particules texte— nuage doré qui se résout en « AUREUS »
 *   9. Carte Belgique  — les villes s'allument et se relient
 *  10. Liquide d'or    — vagues d'or qui se calment sous le logo
 *  11. Supernova       — cœur doré qui explose en mots métier
 *  12. Compteur        — les chiffres de la maison qui défilent
 *
 * ─ 3D (13-22), projection perspective maison, zéro dépendance ─
 *  13. Cube de données — cube tournant, une face par brique métier
 *  14. Globe           — la Terre tourne et s'arrête sur la Belgique
 *  15. Tunnel de fiches— les fiches de paie défilent vers la caméra
 *  16. Pluie de pièces — pièces d'or qui tombent et roulent en volume
 *  17. Hélice brut→net — double hélice, les cotisations s'échappent
 *  18. Tour            — l'entreprise se construit étage par étage
 *  19. Grille barèmes  — survol d'un relief de barèmes sectoriels
 *  20. Carrousel       — anneau de mots métier qui tourne autour du logo
 *  21. Tore doré       — anneau de particules qui se resserre en logo
 *  22. AUREUS en volume— le mot extrudé en profondeur, pivote et se pose
 *
 * - Purement décoratif (zéro dépendance serveur / env, pas de WebGL).
 * - Une seule fois par session navigateur (sessionStorage).
 * - Pages publiques + « / » (qui sert la vitrine ET le dashboard connecté).
 * - Respecte prefers-reduced-motion (logo statique). Clic / tap = passer.
 * - `?intro=N` (0-21) force une animation précise et ignore le sessionStorage
 *   → sert à les revoir une par une sans vider le stockage du navigateur.
 */

import { useEffect, useRef, useState } from 'react';

const SESSION_KEY = 'aureus_splash_v3';
const FADE = 600; // ms de fondu
const GOLD = '#e7c45a';
// Borne une opacité calculée à [0,1]. Indispensable : le canvas IGNORE en silence
// un `rgba(…, -0.06)` — il garde la couleur précédente, sans rien signaler.
const cl = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const TAGLINE = 'LA PAIE BELGE, PROPULSÉE PAR L’IA';

const PUBLIC_PREFIXES = [
  '/vitrine', '/landing', '/login', '/pricing', '/about',
  '/contact', '/blog', '/demo', '/legal',
];

function isPublicPath(path) {
  if (path === '/') return true;
  return PUBLIC_PREFIXES.some((p) => path === p || path.startsWith(p + '/'));
}

// Éclair en zig-zag entre deux points.
function jbolt(x1, y1, x2, y2, disp, seg) {
  const pts = [[x1, y1]];
  for (let i = 1; i < seg; i++) {
    const f = i / seg;
    const mx = x1 + (x2 - x1) * f, my = y1 + (y2 - y1) * f;
    const nx = -(y2 - y1), ny = (x2 - x1), L = Math.hypot(nx, ny) || 1;
    pts.push([mx + nx / L * (Math.random() - 0.5) * disp, my + ny / L * (Math.random() - 0.5) * disp]);
  }
  pts.push([x2, y2]);
  return pts;
}

function logo(ctx, W, H, a, big) {
  ctx.globalAlpha = a;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#fff';
  ctx.font = '800 ' + (big || 34) + 'px ui-sans-serif,system-ui';
  ctx.fillText('AUREUS SOCIAL', W / 2, H / 2 - 4);
  ctx.font = '600 12px ui-monospace,monospace';
  ctx.fillStyle = GOLD;
  ctx.fillText(TAGLINE, W / 2, H / 2 + 24);
  ctx.globalAlpha = 1;
  ctx.textAlign = 'left';
}

// ───────────────────────── 1. JARVIS HUD ─────────────────────────
function vJarvis() {
  const CYAN = '#46e0ff';
  const logs = [['MOTEUR DE PAIE', 'OK'], ['ONSS / DIMONA', 'OK'], ['164 COMMISSIONS PARITAIRES', 'OK'], ['DmfA / BELCOTAX', 'OK'], ['AIDES & SUBSIDES', 'OK'], ['21 AGENTS IA', 'ONLINE']];
  function ring(ctx, cx, cy, rad, a0, a1, col, w, al) { ctx.beginPath(); ctx.arc(cx, cy, rad, a0, a1); ctx.strokeStyle = col; ctx.globalAlpha = al; ctx.lineWidth = w; ctx.stroke(); ctx.globalAlpha = 1; }
  return {
    dur: 6200,
    build() {},
    frame(ctx, W, H, t) {
      const bg = ctx.createRadialGradient(W * 0.64, H * 0.5, 20, W * 0.64, H * 0.5, Math.max(W, H));
      bg.addColorStop(0, '#0b1322'); bg.addColorStop(1, '#05070d');
      ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
      const cx = W * 0.64, cy = H * 0.5, R = Math.min(W * 0.2, 150);
      ring(ctx, cx, cy, R, 0, 6.283, 'rgba(70,224,255,.16)', 1, 1);
      ring(ctx, cx, cy, R, t * 0.8, t * 0.8 + 3.8, CYAN, 2, .85);
      ring(ctx, cx, cy, R * 0.8, -t * 1.1, -t * 1.1 + 1.9, GOLD, 2, .9);
      ring(ctx, cx, cy, R * 0.62, t * 1.7, t * 1.7 + 4.6, 'rgba(231,196,90,.5)', 1.5, 1);
      ctx.save(); ctx.translate(cx, cy); ctx.rotate(t * 0.3);
      for (let i = 0; i < 48; i++) { ctx.rotate(6.283 / 48); ctx.beginPath(); ctx.moveTo(R * 1.06, 0); ctx.lineTo(R * 1.06 + (i % 4 ? 4 : 9), 0); ctx.strokeStyle = i % 4 ? 'rgba(70,224,255,.25)' : GOLD; ctx.lineWidth = 1.5; ctx.stroke(); }
      ctx.restore();
      ctx.save(); ctx.translate(cx, cy); ctx.rotate(t * 1.4);
      const sg = ctx.createLinearGradient(0, 0, R, 0); sg.addColorStop(0, 'rgba(70,224,255,0)'); sg.addColorStop(1, 'rgba(70,224,255,.30)');
      ctx.beginPath(); ctx.moveTo(0, 0); ctx.arc(0, 0, R, -0.5, 0); ctx.closePath(); ctx.fillStyle = sg; ctx.fill(); ctx.restore();
      const pr = R * 0.3 * (1 + 0.12 * Math.sin(t * 4));
      const cg = ctx.createRadialGradient(cx, cy, 0, cx, cy, pr); cg.addColorStop(0, '#fff7e0'); cg.addColorStop(.5, GOLD); cg.addColorStop(1, 'rgba(231,196,90,0)');
      ctx.fillStyle = cg; ctx.beginPath(); ctx.arc(cx, cy, pr, 0, 6.283); ctx.fill();
      ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
      const lx = Math.max(22, W * 0.06), ly0 = H * 0.32, lh = 27, statx = lx + Math.min(300, W * 0.34);
      for (let k = 0; k < logs.length; k++) {
        const ap = 0.4 + k * 0.5; if (t < ap) continue; const y = ly0 + k * lh;
        ctx.font = '700 13px ui-monospace,Menlo,monospace'; ctx.fillStyle = 'rgba(70,224,255,.9)'; ctx.fillText('▸', lx, y);
        ctx.fillStyle = 'rgba(222,234,247,.94)'; ctx.fillText(logs[k][0], lx + 18, y);
        if (t > ap + 0.3) { ctx.textAlign = 'right'; ctx.fillStyle = logs[k][1] === 'ONLINE' ? GOLD : '#5ee08a'; ctx.fillText(logs[k][1], statx, y); ctx.textAlign = 'left'; }
      }
      const total = 0.4 + logs.length * 0.5 + 0.6, p = Math.max(0, Math.min(1, t / total));
      const by = H - 40, bw = Math.min(300, W * 0.34);
      ctx.fillStyle = 'rgba(255,255,255,.10)'; ctx.fillRect(lx, by, bw, 4); ctx.fillStyle = GOLD; ctx.fillRect(lx, by, bw * p, 4);
      ctx.textBaseline = 'alphabetic'; ctx.font = '600 11px ui-monospace,monospace'; ctx.fillStyle = 'rgba(160,180,200,.85)'; ctx.fillText('INITIALISATION ' + Math.round(p * 100) + '%', lx, by - 8);
      if (p >= 1) { const a = Math.min(1, (t - total) / 0.8); ctx.globalAlpha = a; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillStyle = '#fff'; ctx.font = '700 24px ui-sans-serif,system-ui'; ctx.fillText('AUREUS SOCIAL', cx, cy + R + 36); ctx.font = '600 12px ui-monospace,monospace'; ctx.fillStyle = GOLD; ctx.fillText('● SYSTÈME EN LIGNE', cx, cy + R + 58); ctx.globalAlpha = 1; ctx.textAlign = 'left'; }
    },
  };
}

// ───────────────────────── 2. CERVEAU + ÉCLAIRS ─────────────────────────
function vBrain() {
  let cx, cy, Rmax, nodes = [], pairs = [], bolts = [];
  const labels = [['AGENTS IA', -0.15, -0.78], ['BARÈMES CP', -0.92, -0.1], ['DIMONA / ONSS', 0.6, -0.3], ['CONTRATS', -0.5, 0.72], ['RGPD', -0.95, 0.45], ['MOTEUR DE PAIE', 0.42, 0.5], ['DmfA', 0.9, 0.1]];
  function pos(n, ang) { const c = Math.cos(ang), s = Math.sin(ang); return [cx + n.bx * c - n.by * s, cy + n.bx * s + n.by * c]; }
  return {
    dur: 6400,
    build(W, H) {
      cx = W / 2; cy = H * 0.48; Rmax = Math.min(W, H) * 0.34; nodes = []; pairs = []; bolts = [];
      const N = 160;
      for (let i = 0; i < N; i++) { const a = Math.random() * 6.283, r = Math.sqrt(Math.random()) * Rmax; nodes.push({ bx: Math.cos(a) * r * 1.18, by: Math.sin(a) * r * 0.95, tw: Math.random() * 6.283, s: Math.random() * 1.4 + 0.4 }); }
      for (let i = 0; i < N; i++) for (let j = i + 1; j < N; j++) { const dx = nodes[i].bx - nodes[j].bx, dy = nodes[i].by - nodes[j].by; if (dx * dx + dy * dy < (Rmax * 0.3) * (Rmax * 0.3) && Math.random() < 0.45) pairs.push([i, j]); }
    },
    frame(ctx, W, H, t) {
      const g = ctx.createRadialGradient(cx, cy, 10, cx, cy, Math.max(W, H) * 0.7); g.addColorStop(0, '#0a1322'); g.addColorStop(1, '#04060a');
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      const ang = t * 0.12;
      ctx.lineWidth = 0.6;
      for (let i = 0; i < pairs.length; i++) { const a = pos(nodes[pairs[i][0]], ang), b = pos(nodes[pairs[i][1]], ang); ctx.strokeStyle = 'rgba(231,196,90,.10)'; ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.stroke(); }
      for (let i = 0; i < nodes.length; i++) { const n = nodes[i], pp = pos(n, ang), tw = 0.5 + 0.5 * Math.sin(t * 2 + n.tw); ctx.fillStyle = 'rgba(231,196,90,' + (0.32 + 0.5 * tw) + ')'; ctx.beginPath(); ctx.arc(pp[0], pp[1], n.s * (0.8 + tw * 0.6), 0, 6.283); ctx.fill(); }
      if (Math.random() < 0.07) { const ii = (Math.random() * nodes.length) | 0, jj = (Math.random() * nodes.length) | 0, pa = pos(nodes[ii], ang), pb = pos(nodes[jj], ang); bolts.push({ pts: jbolt(pa[0], pa[1], pb[0], pb[1], 20, 7), life: 1 }); }
      for (let i = 0; i < bolts.length; i++) { const bl = bolts[i]; bl.life -= 0.06; if (bl.life <= 0) continue; ctx.globalAlpha = Math.max(0, bl.life); ctx.strokeStyle = 'rgba(127,220,255,.5)'; ctx.lineWidth = 4; ctx.beginPath(); ctx.moveTo(bl.pts[0][0], bl.pts[0][1]); for (let q = 0; q < bl.pts.length; q++) ctx.lineTo(bl.pts[q][0], bl.pts[q][1]); ctx.stroke(); ctx.strokeStyle = '#eaffff'; ctx.lineWidth = 1.3; ctx.beginPath(); ctx.moveTo(bl.pts[0][0], bl.pts[0][1]); for (let q = 0; q < bl.pts.length; q++) ctx.lineTo(bl.pts[q][0], bl.pts[q][1]); ctx.stroke(); ctx.globalAlpha = 1; }
      bolts = bolts.filter((b) => b.life > 0);
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      for (let k = 0; k < labels.length; k++) { if (t < 0.6 + k * 0.42) continue; const x = cx + labels[k][1] * Rmax, y = cy + labels[k][2] * Rmax, pop = Math.min(1, (t - (0.6 + k * 0.42)) / 0.4); ctx.font = '700 12px ui-monospace,Menlo,monospace'; ctx.fillStyle = 'rgba(231,196,90,' + pop + ')'; ctx.fillText(labels[k][0], x, y); }
      const fin = 0.6 + labels.length * 0.42 + 1.1;
      if (t > fin) { const aa = Math.min(1, (t - fin) / 0.7); const sg = ctx.createRadialGradient(cx, cy, 0, cx, cy, Rmax * 1.25); sg.addColorStop(0, 'rgba(255,245,210,' + (0.55 * aa) + ')'); sg.addColorStop(.4, 'rgba(231,196,90,' + (0.18 * aa) + ')'); sg.addColorStop(1, 'rgba(231,196,90,0)'); ctx.fillStyle = sg; ctx.fillRect(0, 0, W, H); logo(ctx, W, H, aa, 30); }
      ctx.textAlign = 'left';
    },
  };
}

// ───────────────────────── 3. TEMPÊTE D'ÉCLAIRS ─────────────────────────
function vStorm() {
  const words = ['PAIE', 'DIMONA', 'DmfA', '164 CP', 'AIDES', '21 AGENTS IA'];
  let events = [];
  return {
    dur: 6400,
    build(W) { events = words.map((w, k) => ({ w, start: 0.5 + k * 0.7, x: W * (0.3 + 0.4 * Math.random()) })); },
    frame(ctx, W, H, t) {
      ctx.fillStyle = '#060510'; ctx.fillRect(0, 0, W, H);
      for (let i = 0; i < 4; i++) { const bx = ((i * 190 + t * 22) % (W + 260)) - 130, by = H * (0.18 + 0.2 * i); const cg = ctx.createRadialGradient(bx, by, 0, bx, by, 170); cg.addColorStop(0, 'rgba(64,58,118,.28)'); cg.addColorStop(1, 'rgba(64,58,118,0)'); ctx.fillStyle = cg; ctx.fillRect(0, 0, W, H); }
      let flash = 0, p, y;
      for (let k = 0; k < events.length; k++) {
        const e = events[k];
        if (t >= e.start && t < e.start + 0.16) {
          flash = Math.max(flash, 1 - (t - e.start) / 0.16);
          const pts = [[e.x, 0]]; y = 0; while (y < H * 0.62) { y += 18 + Math.random() * 26; pts.push([e.x + (Math.random() - 0.5) * 60, Math.min(y, H * 0.62)]); }
          ctx.strokeStyle = 'rgba(185,166,255,.6)'; ctx.lineWidth = 5; ctx.beginPath(); ctx.moveTo(pts[0][0], pts[0][1]); for (p = 0; p < pts.length; p++) ctx.lineTo(pts[p][0], pts[p][1]); ctx.stroke();
          ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.6; ctx.beginPath(); ctx.moveTo(pts[0][0], pts[0][1]); for (p = 0; p < pts.length; p++) ctx.lineTo(pts[p][0], pts[p][1]); ctx.stroke();
        }
        if (t >= e.start && t < e.start + 1.0) { const a = 1 - (t - e.start) / 1.0, sc = 1 + (t - e.start) * 0.25; ctx.save(); ctx.globalAlpha = a; ctx.translate(W / 2, H / 2); ctx.scale(sc, sc); ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.font = '800 44px ui-sans-serif,system-ui'; ctx.fillStyle = GOLD; ctx.fillText(e.w, 0, 0); ctx.restore(); }
      }
      const tEnd = 0.5 + words.length * 0.7 + 0.2;
      if (t >= tEnd && t < tEnd + 0.2) { flash = Math.max(flash, 1 - (t - tEnd) / 0.2); for (let b = 0; b < 3; b++) { const sx = W * (0.28 + 0.22 * b), pp = [[sx, 0]]; y = 0; while (y < H * 0.5) { y += 16 + Math.random() * 22; pp.push([W * 0.5 + (sx - W * 0.5) * (1 - y / (H * 0.5)) + (Math.random() - 0.5) * 40, Math.min(y, H * 0.5)]); } ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(pp[0][0], pp[0][1]); for (p = 0; p < pp.length; p++) ctx.lineTo(pp[p][0], pp[p][1]); ctx.stroke(); } }
      if (t >= tEnd) { const af = Math.min(1, (t - tEnd) / 0.6), rw = (t - tEnd) * 430; if (rw < Math.max(W, H)) { ctx.strokeStyle = 'rgba(231,196,90,' + Math.max(0, 0.5 - (t - tEnd)) + ')'; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(W / 2, H / 2, rw, 0, 6.283); ctx.stroke(); } logo(ctx, W, H, af, 30); }
      if (flash > 0) { ctx.fillStyle = 'rgba(255,255,255,' + (flash * 0.7) + ')'; ctx.fillRect(0, 0, W, H); }
    },
  };
}

// ───────────────────────── 4. VORTEX DORÉ ─────────────────────────
function vVortex() {
  let cx, cy, Rmax, P = []; const tc = 4.0;
  return {
    dur: 6200,
    build(W, H) { cx = W / 2; cy = H / 2; Rmax = Math.min(W, H) * 0.44; P = []; for (let i = 0; i < 440; i++) P.push({ a: Math.random() * 6.283, r: 50 + Math.random() * Rmax, sp: 0.3 + Math.random() * 0.7, sz: Math.random() * 1.6 + 0.5, h: Math.random() }); },
    frame(ctx, W, H, t) {
      ctx.fillStyle = 'rgba(5,6,12,0.26)'; ctx.fillRect(0, 0, W, H);
      let scale, fade = 1, grow = 0;
      if (t < tc) scale = 1 - t / tc * 0.1;
      else if (t < tc + 0.7) { const f = (t - tc) / 0.7; scale = (1 - f) + 0.04 * f; grow = (1 - f) * 1.6; }
      else { const f2 = Math.min(1, (t - (tc + 0.7)) / 1.3); scale = 0.04 + f2 * f2 * 3.4; fade = 1 - f2; }
      for (let i = 0; i < P.length; i++) { const p = P[i], ang = p.a + t * p.sp * (70 / p.r) * 5, dr = p.r * scale, x = cx + Math.cos(ang + dr * 0.012) * dr, y = cy + Math.sin(ang + dr * 0.012) * dr * 0.9, br = 0.4 + 0.6 * p.h; ctx.globalAlpha = Math.max(0, fade * br); ctx.fillStyle = p.h > 0.72 ? '#fff4d6' : GOLD; ctx.beginPath(); ctx.arc(x, y, p.sz * (1 + grow), 0, 6.283); ctx.fill(); }
      ctx.globalAlpha = 1;
      if (t > tc + 0.55 && t < tc + 0.82) { ctx.fillStyle = 'rgba(255,250,230,' + (1 - (t - (tc + 0.55)) / 0.27) + ')'; ctx.fillRect(0, 0, W, H); }
      if (t > tc + 0.7) logo(ctx, W, H, Math.min(1, (t - (tc + 0.7)) / 0.6), 34);
    },
  };
}

// ───────────────────────── 5. PLUIE DE DONNÉES ─────────────────────────
function vRain() {
  let cols, drops = [], fh = 16; const GL = '0123456789€%‰CP200CP124DmfA§AUREUS'; const rev = 3.8;
  return {
    dur: 6200,
    build(W) { cols = Math.floor(W / fh); drops = []; for (let i = 0; i < cols; i++) drops[i] = Math.random() * -30; },
    frame(ctx, W, H, t) {
      ctx.fillStyle = 'rgba(4,6,10,0.12)'; ctx.fillRect(0, 0, W, H);
      ctx.font = '700 14px ui-monospace,Menlo,monospace'; ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
      for (let i = 0; i < cols; i++) { const x = i * fh + 2, y = drops[i] * fh; ctx.fillStyle = '#fff7d8'; ctx.fillText(GL[(Math.random() * GL.length) | 0], x, y); ctx.fillStyle = (i % 5 === 0) ? GOLD : 'rgba(94,224,138,.55)'; ctx.fillText(GL[(Math.random() * GL.length) | 0], x, y - fh); if (y > H && Math.random() > 0.975) drops[i] = Math.random() * -15; drops[i] += 0.55; }
      if (t > rev) { const a = Math.min(1, (t - rev) / 1.0); const vg = ctx.createRadialGradient(W / 2, H / 2, 10, W / 2, H / 2, Math.max(W, H) * 0.6); vg.addColorStop(0, 'rgba(4,6,10,' + (0.85 * a) + ')'); vg.addColorStop(1, 'rgba(4,6,10,0)'); ctx.fillStyle = vg; ctx.fillRect(0, 0, W, H); logo(ctx, W, H, a, 34); }
    },
  };
}

// ───────────────────────── 6. TYPO IMPACT ─────────────────────────
function vTypo() {
  const lines = [['VOTRE PAIE.', 0.4], ['VOS DÉCLARATIONS.', 1.15], ['VOS ÉCHÉANCES.', 1.9], ['GÉRÉES PAR L’IA.', 2.65]];
  const finAt = 3.7;
  return {
    dur: 6000,
    build() {},
    frame(ctx, W, H, t) {
      ctx.fillStyle = '#070708'; ctx.fillRect(0, 0, W, H);
      let shake = 0, k;
      for (k = 0; k < lines.length; k++) { const d = t - lines[k][1]; if (d >= 0 && d < 0.16) shake = Math.max(shake, 1 - d / 0.16); }
      const df = t - finAt; if (df >= 0 && df < 0.18) shake = Math.max(shake, 1 - df / 0.18);
      ctx.save(); ctx.translate((Math.random() - 0.5) * shake * 16, (Math.random() - 0.5) * shake * 16);
      const fz = Math.min(48, W * 0.072);
      for (k = 0; k < lines.length; k++) {
        const st = lines[k][1], end = (k + 1 < lines.length ? lines[k + 1][1] : finAt);
        if (t < st || t >= end) continue;
        const dd = t - st, vis = Math.min(1, dd / 0.14), sc = 1.5 - 0.5 * vis;
        const a = Math.min(vis, Math.min(1, (end - t) / 0.22));
        ctx.save(); ctx.globalAlpha = a; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.translate(W / 2, H / 2); ctx.scale(sc, sc); ctx.font = '800 ' + fz + 'px ui-sans-serif,system-ui'; ctx.fillStyle = '#fff'; ctx.fillText(lines[k][0], 0, 0); ctx.restore();
        const ulw = Math.min(1, dd / 0.28) * (W * 0.3); ctx.globalAlpha = a; ctx.fillStyle = GOLD; ctx.fillRect(W / 2 - ulw / 2, H / 2 + fz * 0.62, ulw, 3); ctx.globalAlpha = 1;
      }
      if (t >= finAt) { const fa = Math.min(1, (t - finAt) / 0.4); ctx.globalAlpha = fa; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillStyle = '#fff'; ctx.font = '900 ' + Math.min(58, W * 0.088) + 'px ui-sans-serif,system-ui'; ctx.fillText('AUREUS', W / 2, H / 2 - 14); ctx.font = '700 ' + Math.min(22, W * 0.035) + 'px ui-sans-serif,system-ui'; ctx.fillStyle = GOLD; ctx.fillText('S O C I A L', W / 2, H / 2 + 24); const u2 = Math.min(1, (t - finAt) / 0.5) * (W * 0.4); ctx.fillStyle = GOLD; ctx.fillRect(W / 2 - u2 / 2, H / 2 + 46, u2, 3); ctx.globalAlpha = 1; ctx.textAlign = 'left'; }
      ctx.restore();
      if (shake > 0) { ctx.fillStyle = 'rgba(255,250,235,' + (shake * 0.18) + ')'; ctx.fillRect(0, 0, W, H); }
    },
  };
}

// ───────────────────────── 7. HYPERESPACE ─────────────────────────
function vWarp() {
  let cx, cy, S = []; const tArr = 4.6;
  return {
    dur: 6200,
    build(W, H) { cx = W / 2; cy = H / 2; S = []; for (let i = 0; i < 320; i++) S.push({ x: (Math.random() - 0.5) * W, y: (Math.random() - 0.5) * H, z: Math.random() * W + 1, pz: 0 }); },
    frame(ctx, W, H, t) {
      ctx.fillStyle = 'rgba(3,4,10,0.32)'; ctx.fillRect(0, 0, W, H);
      let spd; if (t < 3.1) spd = 4 + t * 8; else if (t < tArr) spd = Math.max(2, 32 - (t - 3.1) * 18); else spd = 1.5;
      const foc = W * 0.5;
      for (let i = 0; i < S.length; i++) { const s = S[i]; s.pz = s.z; s.z -= spd; if (s.z < 1) { s.z = W; s.x = (Math.random() - 0.5) * W; s.y = (Math.random() - 0.5) * H; s.pz = s.z; } const k = foc / s.z, pk = foc / s.pz, x2 = cx + s.x * k, y2 = cy + s.y * k, x1 = cx + s.x * pk, y1 = cy + s.y * pk, br = Math.min(1, (W - s.z) / W * 1.3); ctx.strokeStyle = (i % 4 === 0) ? 'rgba(231,196,90,' + br + ')' : 'rgba(210,225,255,' + (br * 0.8) + ')'; ctx.lineWidth = Math.max(0.6, br * 2.2); ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke(); }
      ctx.save(); ctx.translate(cx, cy); for (let rr = 0; rr < 5; rr++) { const rad = ((t * 120 + rr * 90) % 460); ctx.globalAlpha = Math.max(0, 0.25 - (rad / 460) * 0.25); ctx.strokeStyle = GOLD; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.arc(0, 0, rad, 0, 6.283); ctx.stroke(); } ctx.restore(); ctx.globalAlpha = 1;
      if (t > tArr - 0.25 && t < tArr + 0.15) { ctx.fillStyle = 'rgba(255,250,235,' + (1 - Math.abs(t - tArr) / 0.25) + ')'; ctx.fillRect(0, 0, W, H); }
      if (t > tArr) logo(ctx, W, H, Math.min(1, (t - tArr) / 0.5), 36);
    },
  };
}

// ───────────────────────── 8. PARTICULES → « AUREUS » ─────────────────────────
function vText() {
  let cx, cy, P = [];
  return {
    dur: 6400,
    build(W, H) {
      cx = W / 2; cy = H / 2; P = [];
      const off = document.createElement('canvas'); off.width = W; off.height = H;
      const o = off.getContext('2d');
      o.fillStyle = '#fff'; o.textAlign = 'center'; o.textBaseline = 'middle';
      o.font = '900 ' + Math.min(150, W * 0.17) + 'px ui-sans-serif,system-ui';
      o.fillText('AUREUS', W / 2, H / 2);
      const data = o.getImageData(0, 0, W, H).data, step = Math.max(4, Math.floor(W / 240)), targets = [];
      for (let y = 0; y < H; y += step) for (let x = 0; x < W; x += step) { if (data[(y * W + x) * 4 + 3] > 128) targets.push([x, y]); }
      for (let i = 0; i < targets.length; i++) P.push({ x: Math.random() * W, y: Math.random() * H, tx: targets[i][0], ty: targets[i][1], v: 0.05 + Math.random() * 0.05, h: Math.random() });
    },
    frame(ctx, W, H, t) {
      ctx.fillStyle = 'rgba(6,7,14,0.38)'; ctx.fillRect(0, 0, W, H);
      const lock = Math.min(1, t / 3.0);
      for (let i = 0; i < P.length; i++) { const p = P[i]; p.x += (p.tx - p.x) * p.v * (0.5 + lock); p.y += (p.ty - p.y) * p.v * (0.5 + lock); ctx.fillStyle = p.h > 0.7 ? '#fff4d6' : GOLD; ctx.fillRect(p.x, p.y, 1.8, 1.8); }
      if (t > 3.2) { const a = Math.min(1, (t - 3.2) / 0.6); ctx.globalAlpha = a; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillStyle = GOLD; ctx.font = '700 ' + Math.min(20, W * 0.03) + 'px ui-monospace,monospace'; ctx.fillText('S O C I A L   ·   ' + TAGLINE, W / 2, H / 2 + Math.min(85, W * 0.1)); ctx.globalAlpha = 1; ctx.textAlign = 'left'; }
    },
  };
}

// ───────────────────────── 9. CARTE DE BELGIQUE ─────────────────────────
function vBelgium() {
  let cx, cy, sc;
  const cities = [['Bruxelles', 0.50, 0.55], ['Anvers', 0.52, 0.30], ['Gand', 0.34, 0.40], ['Bruges', 0.27, 0.30], ['Liège', 0.75, 0.57], ['Charleroi', 0.45, 0.73], ['Namur', 0.57, 0.70], ['Hasselt', 0.67, 0.42], ['Louvain', 0.55, 0.47], ['Mons', 0.34, 0.74]];
  let edges = [], pulses = [];
  return {
    dur: 6600,
    build(W, H) {
      cx = W / 2; cy = H / 2; sc = Math.min(W, H) * 0.72; edges = []; pulses = [];
      for (let i = 0; i < cities.length; i++) { const d = []; for (let j = 0; j < cities.length; j++) if (j !== i) { const dx = cities[i][1] - cities[j][1], dy = cities[i][2] - cities[j][2]; d.push([dx * dx + dy * dy, j]); } d.sort((a, b) => a[0] - b[0]); for (let k = 0; k < 2; k++) if (i < d[k][1]) edges.push([i, d[k][1]]); }
    },
    frame(ctx, W, H, t) {
      ctx.fillStyle = '#05060c'; ctx.fillRect(0, 0, W, H);
      const px = (c) => [cx + (c[1] - 0.5) * sc, cy + (c[2] - 0.5) * sc];
      ctx.strokeStyle = 'rgba(231,196,90,.16)'; ctx.lineWidth = 1;
      for (let e = 0; e < edges.length; e++) { const a = px(cities[edges[e][0]]), b = px(cities[edges[e][1]]); ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.stroke(); }
      if (Math.random() < 0.18 && edges.length) { const e = edges[(Math.random() * edges.length) | 0]; pulses.push({ e, t: 0 }); }
      pulses = pulses.filter((s) => s.t < 1);
      for (let i = 0; i < pulses.length; i++) { const s = pulses[i]; s.t += 0.03; const a = px(cities[s.e[0]]), b = px(cities[s.e[1]]); const x = a[0] + (b[0] - a[0]) * s.t, y = a[1] + (b[1] - a[1]) * s.t; ctx.fillStyle = 'rgba(255,244,214,' + cl(Math.sin(s.t * Math.PI)) + ')'; ctx.beginPath(); ctx.arc(x, y, 2, 0, 6.283); ctx.fill(); }
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      for (let i = 0; i < cities.length; i++) { const on = t > 0.4 + i * 0.28; if (!on) continue; const p = px(cities[i]), pop = Math.min(1, (t - (0.4 + i * 0.28)) / 0.4), pulse = 1 + 0.4 * Math.sin(t * 3 + i); ctx.fillStyle = 'rgba(231,196,90,' + (0.25 * pop) + ')'; ctx.beginPath(); ctx.arc(p[0], p[1], 8 * pulse, 0, 6.283); ctx.fill(); ctx.fillStyle = '#fff4d6'; ctx.beginPath(); ctx.arc(p[0], p[1], 3, 0, 6.283); ctx.fill(); ctx.font = '600 11px ui-monospace,monospace'; ctx.fillStyle = 'rgba(231,222,200,' + pop + ')'; ctx.fillText(cities[i][0], p[0], p[1] - 14); }
      const fin = 0.4 + cities.length * 0.28 + 0.6;
      if (t > fin) { logo(ctx, W, H, Math.min(1, (t - fin) / 0.6), 30); }
      ctx.textAlign = 'left';
    },
  };
}

// ───────────────────────── 10. LIQUIDE D'OR ─────────────────────────
function vLiquid() {
  return {
    dur: 6000,
    build() {},
    frame(ctx, W, H, t) {
      ctx.fillStyle = '#0a0805'; ctx.fillRect(0, 0, W, H);
      const settle = Math.max(0.18, 1 - t / 4.5);
      for (let L = 3; L >= 0; L--) {
        const baseY = H * (0.52 + L * 0.11), amp = (34 - L * 6) * settle, sp = 1.2 + L * 0.5, k = 0.012 + L * 0.004;
        ctx.beginPath(); ctx.moveTo(0, H);
        for (let x = 0; x <= W; x += 8) { const y = baseY + Math.sin(x * k + t * sp + L) * amp + Math.sin(x * k * 0.5 - t * sp * 0.7) * amp * 0.4; ctx.lineTo(x, y); }
        ctx.lineTo(W, H); ctx.closePath();
        const g = ctx.createLinearGradient(0, baseY - amp, 0, H); const sh = ['#fff0c4', '#e7c45a', '#b8862f', '#7a5618'][L];
        g.addColorStop(0, sh); g.addColorStop(1, '#1c1206'); ctx.globalAlpha = 0.55; ctx.fillStyle = g; ctx.fill(); ctx.globalAlpha = 1;
      }
      if (Math.random() < 0.5) { /* sparks */ }
      if (t > 3.4) { const a = Math.min(1, (t - 3.4) / 0.7); const gl = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, Math.min(W, H) * 0.4); gl.addColorStop(0, 'rgba(10,8,5,' + (0.7 * a) + ')'); gl.addColorStop(1, 'rgba(10,8,5,0)'); ctx.fillStyle = gl; ctx.fillRect(0, 0, W, H); logo(ctx, W, H, a, 34); }
    },
  };
}

// ───────────────────────── 11. SUPERNOVA ─────────────────────────
function vSupernova() {
  let cx, cy, parts = []; const words = ['PAIE', 'DIMONA', 'DmfA', 'CP', 'AIDES', 'IA', 'RGPD', 'ONSS']; const tB = 1.8;
  return {
    dur: 6000,
    build(W, H) { cx = W / 2; cy = H / 2; parts = []; for (let i = 0; i < 220; i++) { const a = Math.random() * 6.283, sp = 2 + Math.random() * 7; parts.push({ a, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, h: Math.random() }); } },
    frame(ctx, W, H, t) {
      ctx.fillStyle = 'rgba(4,4,10,0.34)'; ctx.fillRect(0, 0, W, H);
      if (t < tB) { const pr = (8 + t * 26) * (1 + 0.18 * Math.sin(t * 16)); const cg = ctx.createRadialGradient(cx, cy, 0, cx, cy, pr); cg.addColorStop(0, '#fff7e0'); cg.addColorStop(.5, GOLD); cg.addColorStop(1, 'rgba(231,196,90,0)'); ctx.fillStyle = cg; ctx.beginPath(); ctx.arc(cx, cy, pr, 0, 6.283); ctx.fill(); }
      if (t >= tB) {
        const dt = t - tB;
        for (let i = 0; i < parts.length; i++) { const p = parts[i], x = cx + p.vx * dt * 26, y = cy + p.vy * dt * 26, al = Math.max(0, 1 - dt / 1.6); ctx.fillStyle = p.h > 0.7 ? 'rgba(255,244,214,' + al + ')' : 'rgba(231,196,90,' + al + ')'; ctx.beginPath(); ctx.arc(x, y, 2 * (1 + p.h), 0, 6.283); ctx.fill(); }
        const rw = dt * 380; if (rw < Math.max(W, H)) { ctx.strokeStyle = 'rgba(231,196,90,' + Math.max(0, 0.6 - dt * 0.5) + ')'; ctx.lineWidth = 4; ctx.beginPath(); ctx.arc(cx, cy, rw, 0, 6.283); ctx.stroke(); }
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.font = '800 22px ui-sans-serif,system-ui';
        for (let i = 0; i < words.length; i++) { const a = (i / words.length) * 6.283, d = dt * 150, al = Math.max(0, 0.9 - dt / 1.3); ctx.fillStyle = 'rgba(231,196,90,' + al + ')'; ctx.fillText(words[i], cx + Math.cos(a) * d, cy + Math.sin(a) * d); }
        ctx.textAlign = 'left';
      }
      if (t > 3.3) logo(ctx, W, H, Math.min(1, (t - 3.3) / 0.6), 34);
    },
  };
}

// ───────────────────────── 12. COMPTEUR DE STATS ─────────────────────────
function vCounter() {
  const stats = [[1842, '', 'COMMITS EN 2 MOIS'], [380, '+', 'MODULES'], [164, '', 'COMMISSIONS PARITAIRES'], [21, '', 'AGENTS IA']];
  const step = 1.0, t0s = 0.3;
  return {
    dur: 6200,
    build() {},
    frame(ctx, W, H, t) {
      ctx.fillStyle = '#06060c'; ctx.fillRect(0, 0, W, H);
      const finAt = t0s + stats.length * step;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      for (let k = 0; k < stats.length; k++) {
        const st = t0s + k * step, end = st + step; if (t < st || t >= end || t >= finAt) continue;
        const dd = t - st, ease = Math.min(1, dd / 0.7), a = Math.min(ease < 1 ? 1 : 1, Math.min(1, (end - t) / 0.2));
        const n = Math.round(stats[k][0] * (1 - Math.pow(1 - ease, 3)));
        ctx.globalAlpha = a; ctx.fillStyle = '#fff'; ctx.font = '900 ' + Math.min(96, W * 0.13) + 'px ui-sans-serif,system-ui';
        ctx.fillText(n.toLocaleString('fr-BE') + stats[k][1], W / 2, H / 2 - 8);
        ctx.fillStyle = GOLD; ctx.font = '600 ' + Math.min(18, W * 0.026) + 'px ui-monospace,monospace';
        ctx.fillText(stats[k][2], W / 2, H / 2 + Math.min(58, W * 0.08));
        ctx.globalAlpha = 1;
      }
      if (t >= finAt) logo(ctx, W, H, Math.min(1, (t - finAt) / 0.5), 34);
      ctx.textAlign = 'left';
    },
  };
}

// ═════════════════════ SOCLE 3D — commun aux 10 intros suivantes ═════════════════════
// Pas de WebGL ni de three.js : une intro de marque ne doit rien peser au premier
// rendu ni dépendre d'un GPU. Un point est tourné (autour de Y puis de X) puis
// projeté par k = F3 / (F3 + z) ; ce même k dose la taille ET l'opacité, ce qui
// suffit à faire lire la profondeur à l'œil. Convention : z croît vers le FOND,
// donc un z négatif est DEVANT la caméra (une face n'est visible que si z < 0).
const F3 = 640;

function rot3(x, y, z, ax, ay) {
  const cA = Math.cos(ay), sA = Math.sin(ay);
  const x2 = x * cA + z * sA;
  let z2 = z * cA - x * sA;
  const cB = Math.cos(ax), sB = Math.sin(ax);
  const y2 = y * cB - z2 * sB;
  z2 = y * sB + z2 * cB;
  return [x2, y2, z2];
}
// Renvoie [écranX, écranY, k]. Le garde `d > 1` évite l'explosion quand un point
// passe derrière l'œil (z ≈ -F3) : il est alors écrasé au lieu de partir à l'infini.
function pj(p, W, H, cz) {
  const d = F3 + p[2] + (cz || 0);
  const k = F3 / (d > 1 ? d : 1);
  return [W / 2 + p[0] * k, H / 2 + p[1] * k, k];
}
function fnt(k, base) { return '700 ' + Math.max(8, Math.round(base * k)) + 'px ui-monospace,Menlo,monospace'; }

// ───────────────────────── 13. CUBE DE DONNÉES (3D) ─────────────────────────
function vCube() {
  const F = [[[0, 0, -1], 'PAIE'], [[0, 0, 1], 'ONSS'], [[-1, 0, 0], 'DIMONA'], [[1, 0, 0], 'DmfA'], [[0, -1, 0], 'BARÈMES CP'], [[0, 1, 0], 'AGENTS IA']];
  const V = [[-1, -1, -1], [1, -1, -1], [1, 1, -1], [-1, 1, -1], [-1, -1, 1], [1, -1, 1], [1, 1, 1], [-1, 1, 1]];
  const E = [[0, 1], [1, 2], [2, 3], [3, 0], [4, 5], [5, 6], [6, 7], [7, 4], [0, 4], [1, 5], [2, 6], [3, 7]];
  let S = 100;
  return {
    dur: 6200,
    build(W, H) { S = Math.min(W, H) * 0.2; },
    frame(ctx, W, H, t) {
      const bg = ctx.createRadialGradient(W / 2, H / 2, 10, W / 2, H / 2, Math.max(W, H) * 0.75);
      bg.addColorStop(0, '#0a0e1a'); bg.addColorStop(1, '#04050a');
      ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
      const ay = t * 0.72, ax = 0.45 + Math.sin(t * 0.45) * 0.3, open = cl((t - 4.3) / 1.2), sc = S * (1 + open * 0.5);
      const P = V.map((v) => pj(rot3(v[0] * sc, v[1] * sc, v[2] * sc, ax, ay), W, H, 0));
      ctx.lineWidth = 1.5;
      for (let i = 0; i < E.length; i++) {
        const a = P[E[i][0]], b = P[E[i][1]];
        ctx.strokeStyle = 'rgba(231,196,90,' + cl(0.12 + (a[2] + b[2] - 1.25) * 1.6) + ')';
        ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.stroke();
      }
      for (let i = 0; i < P.length; i++) {
        ctx.fillStyle = 'rgba(255,244,214,' + cl((P[i][2] - 0.62) * 3) + ')';
        ctx.beginPath(); ctx.arc(P[i][0], P[i][1], 2.6, 0, 6.283); ctx.fill();
      }
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      for (let i = 0; i < F.length; i++) {
        const ap = 0.5 + i * 0.3; if (t < ap) continue;
        const d = sc * (1 + open * 0.7), r = rot3(F[i][0][0] * d, F[i][0][1] * d, F[i][0][2] * d, ax, ay);
        if (r[2] > 0) continue;                       // face tournée vers le fond
        const p = pj(r, W, H, 0);
        ctx.font = fnt(p[2], 13);
        ctx.fillStyle = 'rgba(231,196,90,' + cl((-r[2] / d) * 1.5 * Math.min(1, (t - ap) / 0.4)) + ')';
        ctx.fillText(F[i][1], p[0], p[1]);
      }
      ctx.textAlign = 'left';
      if (t > 4.7) logo(ctx, W, H, cl((t - 4.7) / 0.8), 32);
    },
  };
}

// ───────────────────────── 14. GLOBE — CAP SUR LA BELGIQUE (3D) ─────────────────────────
function vGlobe() {
  const rad = (d) => d * Math.PI / 180;
  const LAT = 50.85, LON = 4.35;                       // Bruxelles
  const villes = ['ANVERS', 'GAND', 'LIÈGE', 'BRUGES', 'NAMUR', 'HASSELT'];
  let R = 150, pts = [];
  return {
    dur: 6400,
    build(W, H) {
      R = Math.min(W, H) * 0.3; pts = [];
      for (let la = -80; la <= 80; la += 9) for (let lo = -180; lo < 180; lo += 7) pts.push([rad(la), rad(lo)]);
    },
    frame(ctx, W, H, t) {
      ctx.fillStyle = '#04060c'; ctx.fillRect(0, 0, W, H);
      const e = 1 - Math.pow(1 - Math.min(1, t / 3.2), 3);   // la rotation freine
      const ay = -rad(LON) + (1 - e) * 8.2, ax = rad(LAT) * e;
      for (let i = 0; i < pts.length; i++) {
        const la = pts[i][0], lo = pts[i][1], cla = Math.cos(la);
        const r = rot3(R * cla * Math.sin(lo), -R * Math.sin(la), -R * cla * Math.cos(lo), ax, ay);
        const p = pj(r, W, H, 0), front = r[2] < 0;
        ctx.fillStyle = front ? 'rgba(231,196,90,' + cl((p[2] - 0.78) * 4) + ')' : 'rgba(120,150,190,.12)';
        ctx.beginPath(); ctx.arc(p[0], p[1], front ? 1.5 : 1, 0, 6.283); ctx.fill();
      }
      // Bruxelles, posée sur la sphère et soumise à la même rotation que le reste :
      // quand ax → LAT et ay → −LON, elle vient exactement face caméra.
      const clb = Math.cos(rad(LAT)), rr2 = R * 1.02;
      const pb = pj(rot3(rr2 * clb * Math.sin(rad(LON)), -rr2 * Math.sin(rad(LAT)), -rr2 * clb * Math.cos(rad(LON)), ax, ay), W, H, 0);
      const bx = pb[0], by = pb[1];
      const pulse = 1 + 0.35 * Math.sin(t * 3.4);
      const hg = ctx.createRadialGradient(bx, by, 0, bx, by, 26 * pulse);
      hg.addColorStop(0, 'rgba(255,244,214,.9)'); hg.addColorStop(.45, 'rgba(231,196,90,.35)'); hg.addColorStop(1, 'rgba(231,196,90,0)');
      ctx.fillStyle = hg; ctx.beginPath(); ctx.arc(bx, by, 26 * pulse, 0, 6.283); ctx.fill();
      ctx.strokeStyle = 'rgba(231,196,90,.35)'; ctx.lineWidth = 1;
      for (let o = 0; o < 2; o++) { ctx.beginPath(); ctx.ellipse(W / 2, H / 2, R * (1.16 + o * 0.12), R * (0.2 + o * 0.1), t * 0.25 + o, 0, 6.283); ctx.stroke(); }
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      if (t > 3.0) {
        ctx.font = '800 15px ui-monospace,Menlo,monospace'; ctx.fillStyle = 'rgba(255,244,214,' + cl((t - 3.0) / 0.5) + ')';
        ctx.fillText('BELGIQUE', bx, by - 30);
        for (let i = 0; i < villes.length; i++) {
          const ap = 3.3 + i * 0.22; if (t < ap) continue;
          const a = -1.05 + i * 0.42, rr = R * 0.66;
          ctx.font = '600 11px ui-monospace,Menlo,monospace';
          ctx.fillStyle = 'rgba(231,196,90,' + cl((t - ap) / 0.35) + ')';
          ctx.fillText(villes[i], bx + Math.cos(a) * rr, by + Math.sin(a) * rr);
        }
      }
      ctx.textAlign = 'left';
      if (t > 4.9) logo(ctx, W, H, cl((t - 4.9) / 0.8), 32);
    },
  };
}

// ───────────────────────── 15. TUNNEL DE FICHES (3D) ─────────────────────────
function vDocs() {
  let sheets = [], sw = 130, sh = 175;
  return {
    dur: 6200,
    build(W, H) {
      sw = Math.min(150, W * 0.17); sh = sw * 1.34; sheets = [];
      for (let i = 0; i < 15; i++) sheets.push({ z: 120 + i * 300, x: (Math.random() - 0.5) * W * 0.42, y: (Math.random() - 0.5) * H * 0.3, r: (Math.random() - 0.5) * 0.9, sp: 0.55 + Math.random() * 0.35 });
    },
    frame(ctx, W, H, t) {
      const bg = ctx.createLinearGradient(0, 0, 0, H); bg.addColorStop(0, '#080a12'); bg.addColorStop(1, '#03040a');
      ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
      const spd = 520;
      const vis = sheets.map((s) => { let z = s.z - t * spd * s.sp; while (z < -300) z += 4500; return { s, z }; }).sort((a, b) => b.z - a.z);
      for (let i = 0; i < vis.length; i++) {
        const s = vis[i].s, z = vis[i].z, ang = s.r + t * 0.35;
        const c = [[-sw, -sh], [sw, -sh], [sw, sh], [-sw, sh]].map((q) => {
          const r = rot3(q[0], q[1], 0, 0, ang);
          return pj([r[0] + s.x, r[1] + s.y, r[2] + z], W, H, 0);
        });
        const k = c[0][2], a = cl((k - 0.13) * 2.2) * cl((1.5 - k) * 2);
        if (a <= 0.01) continue;
        ctx.beginPath(); ctx.moveTo(c[0][0], c[0][1]); for (let q = 1; q < 4; q++) ctx.lineTo(c[q][0], c[q][1]); ctx.closePath();
        ctx.fillStyle = 'rgba(240,238,232,' + (a * 0.93) + ')'; ctx.fill();
        ctx.strokeStyle = 'rgba(231,196,90,' + (a * 0.75) + ')'; ctx.lineWidth = 1.4; ctx.stroke();
        for (let l = 0; l < 9; l++) {                    // lignes de la fiche (simulées)
          const f = 0.16 + l * 0.083, wide = l === 0 ? 0.62 : (l % 3 === 0 ? 0.34 : 0.52);
          const gx = c[0][0] + (c[3][0] - c[0][0]) * f, gy = c[0][1] + (c[3][1] - c[0][1]) * f;
          const dx = c[1][0] + (c[2][0] - c[1][0]) * f - gx, dy = c[1][1] + (c[2][1] - c[1][1]) * f - gy;
          ctx.strokeStyle = l === 0 ? 'rgba(231,196,90,' + (a * 0.9) + ')' : 'rgba(60,60,70,' + (a * 0.55) + ')';
          ctx.lineWidth = l === 0 ? 3 : 1.6;
          ctx.beginPath(); ctx.moveTo(gx + dx * 0.1, gy + dy * 0.1); ctx.lineTo(gx + dx * (0.1 + wide), gy + dy * (0.1 + wide)); ctx.stroke();
        }
        const bf = 0.9, bgx = c[0][0] + (c[3][0] - c[0][0]) * bf, bgy = c[0][1] + (c[3][1] - c[0][1]) * bf;
        ctx.strokeStyle = 'rgba(184,134,47,' + (a * 0.9) + ')'; ctx.lineWidth = 5;
        ctx.beginPath(); ctx.moveTo(bgx + (c[1][0] - c[0][0]) * 0.45, bgy + (c[1][1] - c[0][1]) * 0.45); ctx.lineTo(bgx + (c[1][0] - c[0][0]) * 0.9, bgy + (c[1][1] - c[0][1]) * 0.9); ctx.stroke();
      }
      if (t > 4.4) { ctx.fillStyle = 'rgba(4,5,10,' + cl((t - 4.4) / 0.9) * 0.82 + ')'; ctx.fillRect(0, 0, W, H); logo(ctx, W, H, cl((t - 4.6) / 0.8), 34); }
    },
  };
}

// ───────────────────────── 16. PLUIE DE PIÈCES (3D) ─────────────────────────
function vCoins() {
  let P = [], R0 = 20;
  return {
    dur: 6000,
    build(W, H) {
      R0 = Math.max(13, Math.min(W, H) * 0.032); P = [];
      for (let i = 0; i < 46; i++) P.push({ x: (Math.random() - 0.5) * W * 0.95, z: -180 + Math.random() * 900, y0: -H * (0.7 + Math.random() * 2.4), v: 230 + Math.random() * 200, sp: 2 + Math.random() * 4, ph: Math.random() * 6.283, floor: H * 0.34 + Math.random() * H * 0.07 });
    },
    frame(ctx, W, H, t) {
      const bg = ctx.createRadialGradient(W / 2, H * 0.35, 20, W / 2, H * 0.5, Math.max(W, H)); bg.addColorStop(0, '#141008'); bg.addColorStop(1, '#050406');
      ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
      const ord = P.slice().sort((a, b) => b.z - a.z);
      for (let i = 0; i < ord.length; i++) {
        const c = ord[i];
        let y = c.y0 + t * c.v, spin = c.ph + t * c.sp;
        if (y > c.floor) { y = c.floor; spin = c.ph + (c.floor - c.y0) / c.v * c.sp; }   // posée : elle se fige
        const p = pj([c.x, y, c.z], W, H, 0), rx = R0 * p[2], ry = Math.max(1.6, rx * Math.abs(Math.cos(spin)));
        const g = ctx.createLinearGradient(p[0] - rx, p[1] - ry, p[0] + rx, p[1] + ry);
        g.addColorStop(0, '#8a6420'); g.addColorStop(.42, '#fff2c8'); g.addColorStop(.6, GOLD); g.addColorStop(1, '#7a5618');
        ctx.fillStyle = g; ctx.beginPath(); ctx.ellipse(p[0], p[1], rx, ry, 0, 0, 6.283); ctx.fill();
        ctx.strokeStyle = 'rgba(255,246,214,.55)'; ctx.lineWidth = 1; ctx.stroke();
        if (ry > rx * 0.35) { ctx.strokeStyle = 'rgba(122,86,24,.65)'; ctx.beginPath(); ctx.ellipse(p[0], p[1], rx * 0.62, ry * 0.62, 0, 0, 6.283); ctx.stroke(); }
      }
      if (t > 3.6) { const a = cl((t - 3.6) / 0.9); const v = ctx.createRadialGradient(W / 2, H / 2, 10, W / 2, H / 2, Math.max(W, H) * 0.6); v.addColorStop(0, 'rgba(5,4,6,' + (0.86 * a) + ')'); v.addColorStop(1, 'rgba(5,4,6,0)'); ctx.fillStyle = v; ctx.fillRect(0, 0, W, H); logo(ctx, W, H, a, 34); }
    },
  };
}

// ───────────────────────── 17. HÉLICE BRUT → NET (3D) ─────────────────────────
function vHelix() {
  let R = 90, span = 300, esc = [];
  return {
    dur: 6400,
    build(W, H) { R = Math.min(W, H) * 0.16; span = H * 0.62; esc = []; },
    frame(ctx, W, H, t) {
      ctx.fillStyle = '#05060e'; ctx.fillRect(0, 0, W, H);
      const N = 132, ay = t * 0.9;
      const node = (u, off) => {
        const a = u * 8.6 + off + ay, y = (u - 0.5) * span;
        return pj(rot3(Math.cos(a) * R, y, Math.sin(a) * R, 0.12, 0), W, H, 0);
      };
      for (let i = 0; i < N; i += 6) {                    // barreaux (brin A ↔ brin B)
        const u = i / N, a = node(u, 0), b = node(u, Math.PI);
        ctx.strokeStyle = 'rgba(231,196,90,' + cl(0.1 + (a[2] + b[2] - 1.3) * 1.5) + ')'; ctx.lineWidth = 1.4;
        ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.stroke();
      }
      for (let br = 0; br < 2; br++) {
        for (let i = 0; i < N; i++) {
          const p = node(i / N, br * Math.PI), k = p[2];
          ctx.fillStyle = br ? 'rgba(70,224,255,' + cl((k - 0.68) * 3.4) + ')' : 'rgba(231,196,90,' + cl((k - 0.68) * 3.4) + ')';
          ctx.beginPath(); ctx.arc(p[0], p[1], 1.4 + 1.8 * k, 0, 6.283); ctx.fill();
        }
      }
      if (t > 1.0 && esc.length < 90 && Math.random() < 0.5) esc.push({ x: W / 2 + (Math.random() < 0.5 ? -1 : 1) * R * 0.5, y: H / 2 - span * 0.3 + Math.random() * span * 0.5, vx: (Math.random() < 0.5 ? -1 : 1) * (60 + Math.random() * 120), t0: t });
      for (let i = 0; i < esc.length; i++) {
        const e = esc[i], d = t - e.t0; if (d > 1.6) continue;
        ctx.fillStyle = 'rgba(140,160,190,' + cl(0.5 - d / 3.2) + ')';
        ctx.beginPath(); ctx.arc(e.x + e.vx * d, e.y + d * 34, 1.8, 0, 6.283); ctx.fill();
      }
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      if (t > 0.6) { ctx.font = '800 20px ui-sans-serif,system-ui'; ctx.fillStyle = 'rgba(231,196,90,' + cl((t - 0.6) / 0.5) + ')'; ctx.fillText('BRUT', W / 2, H / 2 - span * 0.58); }
      if (t > 2.4) { ctx.font = '600 11px ui-monospace,Menlo,monospace'; ctx.fillStyle = 'rgba(140,160,190,' + cl((t - 2.4) / 0.5) + ')'; ctx.fillText('COTISATIONS · PRÉCOMPTE', W / 2, H / 2); }
      if (t > 3.3) { ctx.font = '800 26px ui-sans-serif,system-ui'; ctx.fillStyle = 'rgba(70,224,255,' + cl((t - 3.3) / 0.5) + ')'; ctx.fillText('NET', W / 2, H / 2 + span * 0.58); }
      ctx.textAlign = 'left';
      if (t > 4.7) { ctx.fillStyle = 'rgba(5,6,14,' + cl((t - 4.7) / 0.8) * 0.8 + ')'; ctx.fillRect(0, 0, W, H); logo(ctx, W, H, cl((t - 4.9) / 0.7), 32); }
    },
  };
}

// ───────────────────────── 18. LA TOUR QUI SE CONSTRUIT (3D) ─────────────────────────
function vTower() {
  const mods = ['CONTRATS', 'DIMONA', 'PAIE', 'DÉCLARATIONS', 'AIDES', 'AGENTS IA'];
  let bw = 90, bh = 42;
  return {
    dur: 6400,
    build(W, H) { bw = Math.min(105, W * 0.13); bh = Math.min(46, H * 0.062); },
    frame(ctx, W, H, t) {
      const bg = ctx.createLinearGradient(0, 0, 0, H); bg.addColorStop(0, '#070b16'); bg.addColorStop(1, '#020308');
      ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
      const ay = 0.55 + t * 0.32, ax = 0.34, base = mods.length * bh * 0.5;
      const corners = (lvl) => [[-bw, base - lvl * bh, -bw], [bw, base - lvl * bh, -bw], [bw, base - lvl * bh, bw], [-bw, base - lvl * bh, bw]]
        .map((q) => pj(rot3(q[0], q[1], q[2], ax, ay), W, H, 120));
      ctx.textBaseline = 'middle';
      for (let l = 0; l < mods.length; l++) {
        const ap = 0.4 + l * 0.55; if (t < ap) continue;
        const grow = Math.min(1, (t - ap) / 0.45), a = grow;
        const lo = corners(l), hi = corners(l + grow);
        ctx.strokeStyle = 'rgba(231,196,90,' + (a * 0.75) + ')'; ctx.lineWidth = 1.5;
        for (let q = 0; q < 4; q++) {                     // montants + plancher + plafond
          const n = (q + 1) % 4;
          ctx.beginPath(); ctx.moveTo(lo[q][0], lo[q][1]); ctx.lineTo(lo[n][0], lo[n][1]); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(hi[q][0], hi[q][1]); ctx.lineTo(hi[n][0], hi[n][1]); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(lo[q][0], lo[q][1]); ctx.lineTo(hi[q][0], hi[q][1]); ctx.stroke();
        }
        ctx.fillStyle = 'rgba(231,196,90,' + (a * 0.07) + ')';
        ctx.beginPath(); ctx.moveTo(hi[0][0], hi[0][1]); for (let q = 1; q < 4; q++) ctx.lineTo(hi[q][0], hi[q][1]); ctx.closePath(); ctx.fill();
        if (grow >= 1) {
          const lbl = pj(rot3(bw * 1.5, base - l * bh - bh * 0.5, 0, ax, ay), W, H, 120);
          ctx.textAlign = 'left'; ctx.font = fnt(lbl[2], 12);
          ctx.fillStyle = 'rgba(231,222,200,' + cl((t - ap - 0.45) / 0.4) + ')';
          ctx.fillText(mods[l], lbl[0] + 8, lbl[1]);
          ctx.fillStyle = GOLD; ctx.beginPath(); ctx.arc(lbl[0], lbl[1], 2.4, 0, 6.283); ctx.fill();
        }
      }
      ctx.textAlign = 'left';
      if (t > 4.5) { const a = cl((t - 4.5) / 0.9); ctx.fillStyle = 'rgba(3,4,10,' + (a * 0.84) + ')'; ctx.fillRect(0, 0, W, H); logo(ctx, W, H, a, 32); }
    },
  };
}

// ───────────────────────── 19. RELIEF DES BARÈMES (3D) ─────────────────────────
function vGrid3D() {
  const NX = 13, NZ = 15;
  let gap = 62;
  return {
    dur: 6200,
    build(W, H) { gap = Math.max(46, Math.min(W, H) * 0.085); },
    frame(ctx, W, H, t) {
      const bg = ctx.createLinearGradient(0, 0, 0, H); bg.addColorStop(0, '#04060f'); bg.addColorStop(.55, '#070a16'); bg.addColorStop(1, '#020306');
      ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
      const scroll = (t * 150) % gap, fy = H * 0.16;
      ctx.lineWidth = 1;
      for (let j = 0; j < NZ; j++) {                       // sol : lignes transversales
        const z = j * gap - scroll, a = cl(1 - j / NZ) * 0.4;
        const l = pj([-NX * gap * 0.5, fy, z], W, H, 150), r = pj([NX * gap * 0.5, fy, z], W, H, 150);
        ctx.strokeStyle = 'rgba(231,196,90,' + a + ')';
        ctx.beginPath(); ctx.moveTo(l[0], l[1]); ctx.lineTo(r[0], r[1]); ctx.stroke();
      }
      for (let i = 0; i <= NX; i++) {                      // sol : fuyantes
        const x = (i - NX / 2) * gap;
        const n = pj([x, fy, -scroll], W, H, 150), f = pj([x, fy, NZ * gap], W, H, 150);
        ctx.strokeStyle = 'rgba(231,196,90,.14)';
        ctx.beginPath(); ctx.moveTo(n[0], n[1]); ctx.lineTo(f[0], f[1]); ctx.stroke();
      }
      for (let j = NZ - 1; j >= 0; j--) {                  // barres, du fond vers l'avant
        for (let i = 0; i < NX; i++) {
          const x = (i - NX / 2 + 0.5) * gap, z = j * gap - scroll + gap * 0.5;
          const h = (0.35 + 0.65 * Math.abs(Math.sin(i * 1.7 + (j + t * 2.4) * 0.55))) * gap * 1.9;
          const b = pj([x, fy, z], W, H, 150), u = pj([x, fy - h, z], W, H, 150), w = gap * 0.3 * b[2];
          const a = cl(1 - j / NZ) * 0.85;
          const g = ctx.createLinearGradient(0, u[1], 0, b[1]);
          g.addColorStop(0, 'rgba(255,244,214,' + a + ')'); g.addColorStop(1, 'rgba(184,134,47,' + (a * 0.25) + ')');
          ctx.fillStyle = g; ctx.fillRect(b[0] - w / 2, u[1], w, b[1] - u[1]);
          ctx.fillStyle = 'rgba(255,250,235,' + a + ')'; ctx.fillRect(b[0] - w / 2, u[1] - 2, w, 2);
        }
      }
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      if (t > 0.8 && t < 4.4) { ctx.font = '600 12px ui-monospace,Menlo,monospace'; ctx.fillStyle = 'rgba(231,196,90,' + cl(Math.min((t - 0.8) / 0.5, (4.4 - t) / 0.5)) + ')'; ctx.fillText('BARÈMES SECTORIELS · SUIVIS EN CONTINU', W / 2, H * 0.14); }
      ctx.textAlign = 'left';
      if (t > 4.4) { const a = cl((t - 4.4) / 0.9); ctx.fillStyle = 'rgba(3,4,8,' + (a * 0.85) + ')'; ctx.fillRect(0, 0, W, H); logo(ctx, W, H, a, 34); }
    },
  };
}

// ───────────────────────── 20. CARROUSEL DE MOTS (3D) ─────────────────────────
function vCarousel() {
  const words = ['PAIE', 'ONSS', 'DIMONA', 'DmfA', 'CONTRATS', 'CONGÉS', 'AIDES', 'BELCOTAX', 'RGPD', 'AGENTS IA'];
  let R = 200;
  return {
    dur: 6200,
    build(W, H) { R = Math.min(W * 0.42, H * 0.7); },
    frame(ctx, W, H, t) {
      const bg = ctx.createRadialGradient(W / 2, H / 2, 10, W / 2, H / 2, Math.max(W, H) * 0.7);
      bg.addColorStop(0, '#0b0d18'); bg.addColorStop(1, '#04050a');
      ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
      const spin = t * 0.62, tilt = 0.22 + Math.sin(t * 0.4) * 0.12, close = cl((t - 4.0) / 1.3);
      const rr = R * (1 - close * 0.72);
      const items = words.map((w, i) => {
        const a = (i / words.length) * 6.283 + spin;
        return { w, p: pj(rot3(Math.cos(a) * rr, 0, Math.sin(a) * rr, tilt, 0), W, H, 0) };
      }).sort((a, b) => a.p[2] - b.p[2]);
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      for (let i = 0; i < items.length; i++) {
        const k = items[i].p[2], a = cl((k - 0.55) * 2.4) * (1 - close * 0.85);
        ctx.font = '800 ' + Math.max(10, Math.round(30 * k)) + 'px ui-sans-serif,system-ui';
        ctx.fillStyle = 'rgba(231,196,90,' + a + ')';
        ctx.fillText(items[i].w, items[i].p[0], items[i].p[1]);
      }
      ctx.textAlign = 'left';
      const pr = 8 + 26 * close + 3 * Math.sin(t * 5);
      const cg = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, pr * 2.4);
      cg.addColorStop(0, 'rgba(255,247,224,' + (0.5 + close * 0.5) + ')'); cg.addColorStop(.5, 'rgba(231,196,90,.3)'); cg.addColorStop(1, 'rgba(231,196,90,0)');
      ctx.fillStyle = cg; ctx.beginPath(); ctx.arc(W / 2, H / 2, pr * 2.4, 0, 6.283); ctx.fill();
      if (t > 4.9) logo(ctx, W, H, cl((t - 4.9) / 0.7), 32);
    },
  };
}

// ───────────────────────── 21. TORE DORÉ (3D) ─────────────────────────
function vTorus() {
  let Rg = 150, Rp = 46, pts = [];
  return {
    dur: 6000,
    build(W, H) {
      Rg = Math.min(W, H) * 0.27; Rp = Rg * 0.31; pts = [];
      for (let u = 0; u < 62; u++) for (let v = 0; v < 16; v++) pts.push([u / 62 * 6.283, v / 16 * 6.283, Math.random()]);
    },
    frame(ctx, W, H, t) {
      ctx.fillStyle = 'rgba(4,5,10,0.3)'; ctx.fillRect(0, 0, W, H);
      const ax = 0.75 + Math.sin(t * 0.5) * 0.35, ay = t * 0.85;
      const burst = cl((t - 3.8) / 1.1), sq = 1 - burst * 0.55;
      for (let i = 0; i < pts.length; i++) {
        const u = pts[i][0] + t * 0.35, v = pts[i][1] + t * 1.15, h = pts[i][2];
        const ring = (Rg + Rp * Math.cos(v)) * sq;
        const r = rot3(ring * Math.cos(u), Rp * Math.sin(v) * sq, ring * Math.sin(u), ax, ay);
        const p = pj(r, W, H, 0), k = p[2];
        ctx.fillStyle = h > 0.78 ? 'rgba(255,246,220,' + cl((k - 0.6) * 3) + ')' : 'rgba(231,196,90,' + cl((k - 0.62) * 2.6) + ')';
        ctx.beginPath(); ctx.arc(p[0], p[1], 0.9 + 1.7 * k, 0, 6.283); ctx.fill();
      }
      if (t > 4.3 && t < 4.65) { ctx.fillStyle = 'rgba(255,250,232,' + (1 - (t - 4.3) / 0.35) * 0.75 + ')'; ctx.fillRect(0, 0, W, H); }
      if (t > 4.4) logo(ctx, W, H, cl((t - 4.4) / 0.7), 34);
    },
  };
}

// ───────────────────────── 22. AUREUS EN VOLUME (3D) ─────────────────────────
function vExtrude() {
  const LAYERS = 6;
  let P = [], depth = 9;
  return {
    dur: 6400,
    build(W, H) {
      P = []; depth = Math.max(6, Math.min(W, H) * 0.014);
      const off = document.createElement('canvas'); off.width = W; off.height = H;
      const o = off.getContext('2d');
      o.fillStyle = '#fff'; o.textAlign = 'center'; o.textBaseline = 'middle';
      o.font = '900 ' + Math.min(160, W * 0.19) + 'px ui-sans-serif,system-ui';
      o.fillText('AUREUS', W / 2, H / 2);
      const data = o.getImageData(0, 0, W, H).data, step = Math.max(6, Math.floor(W / 150));
      for (let y = 0; y < H; y += step) for (let x = 0; x < W; x += step) {
        if (data[(y * W + x) * 4 + 3] > 128) P.push([x - W / 2, y - H / 2, Math.random()]);
      }
    },
    frame(ctx, W, H, t) {
      ctx.fillStyle = 'rgba(5,6,12,0.42)'; ctx.fillRect(0, 0, W, H);
      const settle = 1 - Math.pow(1 - Math.min(1, t / 4.0), 3);
      const ay = (1 - settle) * 2.6 - 0.55 * (1 - settle) + Math.sin(t * 0.7) * 0.32 * (1 - settle * 0.72);
      const ax = (1 - settle) * 0.5;
      for (let l = LAYERS - 1; l >= 0; l--) {
        const z = (l - (LAYERS - 1) / 2) * depth, front = 1 - l / LAYERS;
        for (let i = 0; i < P.length; i++) {
          const r = rot3(P[i][0], P[i][1], z, ax, ay), p = pj(r, W, H, 0);
          ctx.fillStyle = l === 0
            ? (P[i][2] > 0.8 ? 'rgba(255,247,222,.95)' : 'rgba(231,196,90,.92)')
            : 'rgba(150,110,36,' + (0.18 + front * 0.3) + ')';
          ctx.fillRect(p[0], p[1], 2 * p[2], 2 * p[2]);
        }
      }
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      if (t > 3.6) {
        const a = cl((t - 3.6) / 0.7);
        ctx.font = '700 ' + Math.min(20, Math.max(11, W * 0.028)) + 'px ui-monospace,monospace';
        ctx.fillStyle = 'rgba(231,196,90,' + a + ')';
        ctx.fillText('S O C I A L   ·   ' + TAGLINE, W / 2, H / 2 + Math.min(92, W * 0.11));
      }
      ctx.textAlign = 'left';
    },
  };
}

// Exporté pour le test : chaque variante doit pouvoir être JOUÉE image par image
// (voir __tests__/splash-intro-animations.test.js), pas seulement exister.
export const VARIANTS = [
  vJarvis, vBrain, vStorm, vVortex, vRain, vTypo, vWarp, vText, vBelgium, vLiquid, vSupernova, vCounter,
  vCube, vGlobe, vDocs, vCoins, vHelix, vTower, vGrid3D, vCarousel, vTorus, vExtrude,
];

export default function SplashIntro() {
  const [active, setActive] = useState(false);
  const [fading, setFading] = useState(false);
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const forcedRef = useRef(-1);

  useEffect(() => {
    // `?intro=N` — prévisualisation d'une animation précise. Ignore le
    // sessionStorage (sinon impossible de la rejouer sans vider le navigateur)
    // et s'affiche quelle que soit la page, y compris connecté.
    let forced = -1;
    try {
      const q = new URLSearchParams(window.location.search).get('intro');
      if (q !== null) { const n = parseInt(q, 10); if (n >= 0 && n < VARIANTS.length) forced = n; }
    } catch { /* URL exotique */ }
    forcedRef.current = forced;
    let seen = false;
    try { seen = sessionStorage.getItem(SESSION_KEY) === '1'; } catch { /* storage indispo */ }
    const path = window.location.pathname || '/';
    if (forced >= 0 || (!seen && isPublicPath(path))) setActive(true);
  }, []);

  const dismiss = () => {
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* ignore */ }
    setFading(true);
    window.setTimeout(() => setActive(false), FADE);
  };

  useEffect(() => {
    if (!active) return undefined;
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const idx = forcedRef.current >= 0 ? forcedRef.current : Math.floor(Math.random() * VARIANTS.length);
    const variant = VARIANTS[idx]();
    let W = 0, H = 0, dpr = 1;
    const t0 = performance.now();

    function build() {
      const rect = canvas.getBoundingClientRect();
      dpr = Math.min(2, window.devicePixelRatio || 1);
      W = rect.width; H = rect.height;
      canvas.width = Math.floor(W * dpr); canvas.height = Math.floor(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = '#05060c'; ctx.fillRect(0, 0, W, H);
      variant.build(W, H, dpr);
    }
    function staticLogo() {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = '#06060c'; ctx.fillRect(0, 0, W, H);
      logo(ctx, W, H, 1, 34);
    }
    function frame() {
      variant.frame(ctx, W, H, (performance.now() - t0) / 1000);
      rafRef.current = requestAnimationFrame(frame);
    }

    build();
    const onResize = () => build();
    window.addEventListener('resize', onResize);
    let timer;
    if (reduced) { staticLogo(); timer = window.setTimeout(dismiss, 1600); }
    else { rafRef.current = requestAnimationFrame(frame); timer = window.setTimeout(dismiss, variant.dur); }

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  if (!active) return null;

  return (
    <div
      onClick={dismiss}
      role="presentation"
      style={{ position: 'fixed', inset: 0, zIndex: 999999, background: '#06060c', opacity: fading ? 0 : 1, transition: `opacity ${FADE}ms ease`, cursor: 'pointer' }}
    >
      <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%' }} />
      <span style={{ position: 'absolute', bottom: 18, left: 0, right: 0, textAlign: 'center', fontSize: 11, color: 'rgba(158,155,147,.55)', fontFamily: "'Inter',system-ui,sans-serif", letterSpacing: '.5px', pointerEvents: 'none' }}>Passer</span>
    </div>
  );
}
